import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import {
  extractBulkEditViewActionComponentReadingsIdentifiers,
  extractBulkEditViewActionComponentSettingsIdentifiers,
} from '@/features/equipment-browser/equipment-bulk-edit/extract-identifiers';
import {
  deviceIdsAtom,
  deviceKindAtom,
} from '@/features/equipment-browser/equipment-bulk-edit/state/bulk-edit-params.state';
import { useSubscribeEBReadings } from '@/features/equipment-browser/subscriptions/use-subscribe-eb-readings';
import { useSubscribeEBSettings } from '@/features/equipment-browser/subscriptions/use-subscribe-eb-settings';
import { useGetEquipmentBulkEditView } from '@/hooks/query-hooks/gaia-api/equipment-browser';
import { useCurrentFacility } from '@/hooks/use-current-facility';

/**
 * Encapsulates all data fetching, identifier extraction, and WebSocket subscription
 * logic needed by the bulk edit drawer. Returns the loaded bulk edit view and device kind
 * so the drawer can decide whether to render.
 */
export const useEquipmentBulkEditLoader = () => {
  const { agentId } = useCurrentFacility();
  const deviceKind = useAtomValue(deviceKindAtom);
  const deviceIds = useAtomValue(deviceIdsAtom);

  // Fetch the bulk edit view configuration
  const { data: bulkEditView } = useGetEquipmentBulkEditView({
    agentId: agentId,
    deviceKind: deviceKind,
    deviceIds: deviceIds,
  });

  // Extract setting and reading identifiers from the bulk edit view config
  const settingsIdentifiers = useMemo(
    () => extractBulkEditViewActionComponentSettingsIdentifiers(bulkEditView?.config),
    [bulkEditView]
  );

  const readingsIdentifiers = useMemo(
    () => extractBulkEditViewActionComponentReadingsIdentifiers(bulkEditView?.config),
    [bulkEditView]
  );

  // Subscribe to the settings and readings from the view config
  useSubscribeEBSettings(settingsIdentifiers);
  useSubscribeEBReadings(readingsIdentifiers);

  return { bulkEditView, deviceKind };
};
